import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SEMI_FRESH } from '../../lib/queryPresets';
import * as svc from '../../services/files/filesService';

export const filesKeys = {
  folders: (dossierId: string, parentId: string | null) =>
    ['dossier-folders', dossierId, parentId ?? 'root'] as const,
  files: (dossierId: string, folderId: string | null) =>
    ['dossier-files', dossierId, folderId ?? 'root'] as const,
};

export function useFolders(dossierId: string | undefined, parentId: string | null) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: filesKeys.folders(dossierId ?? '', parentId),
    queryFn: () => svc.listFolders(dossierId!, parentId),
    enabled: !!dossierId,
  });
}

export function useFiles(dossierId: string | undefined, folderId: string | null) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: filesKeys.files(dossierId ?? '', folderId),
    queryFn: () => svc.listFiles(dossierId!, folderId),
    enabled: !!dossierId,
  });
}

function invalidateDossierFolders(qc: ReturnType<typeof useQueryClient>, dossierId: string) {
  qc.invalidateQueries({ queryKey: ['dossier-folders'] });
  qc.invalidateQueries({ queryKey: ['dossier-folders-all', dossierId] });
}

function invalidateDossierFiles(qc: ReturnType<typeof useQueryClient>, dossierId: string) {
  qc.invalidateQueries({ queryKey: ['dossier-files'] });
  qc.invalidateQueries({ queryKey: ['dossier-files-all', dossierId] });
}

export function useCreateFolder(dossierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: svc.createFolder,
    onSuccess: () => {
      invalidateDossierFolders(qc, dossierId);
    },
  });
}

export function useRenameFolder(dossierId: string, _parentId?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ folderId, name }: { folderId: string; name: string }) =>
      svc.renameFolder(folderId, name),
    onSuccess: () => {
      invalidateDossierFolders(qc, dossierId);
    },
  });
}

export function useDeleteFolder(dossierId: string, _parentId?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (folderId: string) => svc.deleteFolder(folderId),
    onSuccess: () => {
      invalidateDossierFolders(qc, dossierId);
    },
  });
}

export function useUploadFile(dossierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      file: File;
      uploaderId: string;
      folderId: string | null;
      onProgress?: (pct: number) => void;
    }) =>
      svc.uploadFile({
        dossierId,
        folderId: input.folderId,
        uploaderId: input.uploaderId,
        file: input.file,
        onProgress: input.onProgress,
      }),
    onSuccess: () => {
      invalidateDossierFiles(qc, dossierId);
    },
  });
}

export function useDeleteFile(dossierId: string, _folderId?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) => svc.deleteFile(fileId),
    onSuccess: () => {
      invalidateDossierFiles(qc, dossierId);
    },
  });
}

export function useAllDossierFiles(dossierId: string | undefined) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: ['dossier-files-all', dossierId ?? ''],
    queryFn: () => svc.listAllFiles(dossierId!),
    enabled: !!dossierId,
  });
}

/** Vue globale « Fichiers » — tous les fichiers visibles, avec leur dossier. */
export function useAllCabinetFiles() {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: ['cabinet-files-all'],
    queryFn: () => svc.listAllCabinetFiles(),
  });
}

/** Fiche contact — fichiers partagés avec le client dans ses dossiers. */
export function useSharedFilesForDossiers(dossierIds: string[]) {
  const key = [...dossierIds].sort().join(',');
  return useQuery({
    ...SEMI_FRESH,
    queryKey: ['shared-files-for-dossiers', key],
    queryFn: () => svc.listSharedFilesForDossiers(dossierIds),
    enabled: dossierIds.length > 0,
  });
}

export function useAllDossierFolders(dossierId: string | undefined) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: ['dossier-folders-all', dossierId ?? ''],
    queryFn: () => svc.listAllFolders(dossierId!),
    enabled: !!dossierId,
  });
}

export function useApplyFolderTemplate(dossierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (template: 'conseil' | 'contentieux') =>
      svc.applyFolderTemplate(dossierId, template),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dossier-folders'] });
      qc.invalidateQueries({ queryKey: ['dossier-folders-all', dossierId] });
    },
  });
}

export function useMoveFile(dossierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, toFolderId }: { fileId: string; toFolderId: string | null }) =>
      svc.moveFile(fileId, toFolderId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dossier-files'] });
      qc.invalidateQueries({ queryKey: ['dossier-files-all', dossierId] });
    },
  });
}

export function useSetFileVisibility(dossierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, visibility }: { fileId: string; visibility: 'internal' | 'shared_client' }) =>
      svc.setFileVisibility(fileId, visibility),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dossier-files'] });
      qc.invalidateQueries({ queryKey: ['dossier-files-all', dossierId] });
    },
  });
}
